/** Validarea completă: schemă, reguli, niveluri de echipare, estimări. Oglinda lui `validate` din Rust. */
import schema from '../../data/schema/vehicle.schema.json';
import { rules } from './rules';
import { checkSchema } from './schemaCheck';
import type { Issue, Report, VehicleDefinition } from './types';

type Json = unknown;

const isObj = (v: Json): v is Record<string, Json> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** RFC 7386: obiectele se combină recursiv, restul se înlocuiește, null șterge. */
export function mergePatch(target: Json, patch: Json): Json {
  if (!isObj(patch)) return structuredClone(patch);
  const out: Record<string, Json> = isObj(target) ? { ...target } : {};
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) delete out[k];
    else out[k] = mergePatch(out[k], v);
  }
  return out;
}

export function getPath(v: Json, path: string): Json | undefined {
  let cur: Json = v;
  for (const key of path.split('.')) {
    if (Array.isArray(cur)) cur = cur[Number(key)];
    else if (isObj(cur)) cur = cur[key];
    else return undefined;
    if (cur === undefined) return undefined;
  }
  return cur;
}

function validateLevel(value: Json, brandIds: string[]): { issues: Issue[]; def: VehicleDefinition | null } {
  const issues: Issue[] = [];
  checkSchema(schema as Record<string, unknown>, value, '', issues);
  if (issues.some((i) => i.severity === 'error')) return { issues, def: null };
  const def = value as VehicleDefinition;
  issues.push(...rules(def, brandIds));
  return { issues, def };
}

/** Aplică nivelul de echipare peste definiție. */
export function applyTrim(def: VehicleDefinition, trimId: string): VehicleDefinition {
  const t = def.trims.find((x) => x.id === trimId);
  return t ? (mergePatch(def, t.overrides) as VehicleDefinition) : def;
}

export function validate(value: Json, brandIds: string[]): Report {
  const { issues, def } = validateLevel(value, brandIds);
  if (def) {
    const base = new Set(issues.map((x) => `${x.code}|${x.path}|${x.message}`));
    def.trims.forEach((trim, i) => {
      const merged = mergePatch(value, trim.overrides);
      for (const it of validateLevel(merged, brandIds).issues) {
        if (base.has(`${it.code}|${it.path}|${it.message}`)) continue;
        issues.push({ ...it, trim: trim.id, path: `trims.${i}.overrides → ${it.path}` });
      }
    });
    def.estimates.forEach((est, i) => {
      if (getPath(value, est.path) === undefined) issues.push({ severity: 'error', code: 'estimates.path', path: `estimates.${i}.path`, message: `câmpul estimat „${est.path}” nu există` });
    });
  }
  const errors = issues.filter((i) => i.severity === 'error').length;
  const id = isObj(value) && typeof value.id === 'string' ? value.id : '?';
  return { id, ok: errors === 0, errors, warnings: issues.length - errors, issues };
}
